import './dashboard.css'
import { reportPageView } from './pageview.js'
import { buildLoginUrl } from './auth-state.js'
import { capabilityEnabled } from './rollout.js'
import { getLocale, onLocaleChange } from './locale.js'
import { getMessages } from './i18n.js'

const statusEl = document.getElementById('teams-status')
const teamForm = document.getElementById('team-form')
const inviteAcceptForm = document.getElementById('invite-accept-form')
const inviteForm = document.getElementById('invite-form')
const projectForm = document.getElementById('project-form')
const teamPicker = document.getElementById('team-picker')
const inviteTeamSelect = document.getElementById('team-invite-select')
const projectTeamSelect = document.getElementById('team-project-select')
const projectIdSelect = document.getElementById('team-project-id')
const summaryRoot = document.getElementById('team-summary')
const membersBody = document.getElementById('team-members-body')
const teamProjectsBody = document.getElementById('team-projects-body')
const teamInvitesBody = document.getElementById('team-invites-body')
const teamPoliciesRoot = document.getElementById('team-policies')
const createPanel = document.getElementById('teams-create-panel')
const invitePanel = document.getElementById('teams-invite-panel')
const projectPanel = document.getElementById('teams-project-panel')

let capabilitySnapshot = null
let teamsCache = []
let projectsCache = []
let teamOverviewCache = null
let selectedTeamId = ''

function t() {
  return getMessages('pages/teams', getLocale())
}

function formatDate(ts) {
  return ts
    ? new Date(ts * 1000).toLocaleString(getLocale() === 'en' ? 'en-US' : 'zh-CN', { hour12: false })
    : '-'
}

function setStatus(text, isError = false) {
  statusEl.textContent = text
  statusEl.style.color = isError ? '#c2410c' : ''
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options })
  const body = await res.json().catch(() => ({}))
  if (res.status === 401) {
    window.location.href = buildLoginUrl('/teams')
    return null
  }
  if (!res.ok) throw new Error(body.detail || t().requestFailed)
  return body
}

function teamCanManage(team) {
  return Boolean(team?.can_manage)
}

function teamRoleLabel(role) {
  return t().roles[role] || role
}

function teamPolicyLabel(key) {
  return t().policies[key] || key
}

function currentTeam() {
  return teamsCache.find((item) => item.id === selectedTeamId) || null
}

function currentCanManage() {
  return teamCanManage(currentTeam())
}

function renderRoleOptions(select, currentRole = 'member') {
  if (!select) return
  select.innerHTML = `
    <option value="member"${currentRole === 'member' ? ' selected' : ''}>${teamRoleLabel('member')}</option>
    <option value="admin"${currentRole === 'admin' ? ' selected' : ''}>${teamRoleLabel('admin')}</option>
  `
}

function setSelectedTeam(teamId) {
  selectedTeamId = teamId || ''
  teamPicker.value = selectedTeamId
  inviteTeamSelect.value = selectedTeamId
  projectTeamSelect.value = selectedTeamId
}

function renderTeamOptions() {
  const items = teamsCache
  const placeholder = `<option value="">${t().selectTeam}</option>`
  const options = items
    .map(
      (item) =>
        `<option value="${item.id}">${item.name} · ${teamRoleLabel(item.member_role)}</option>`,
    )
    .join('')

  teamPicker.innerHTML = items.length ? options : placeholder
  inviteTeamSelect.innerHTML = items.length ? `${placeholder}${options}` : placeholder
  projectTeamSelect.innerHTML = items.length ? `${placeholder}${options}` : placeholder

  if (!selectedTeamId && items.length) {
    setSelectedTeam(items[0].id)
  } else {
    setSelectedTeam(selectedTeamId)
  }
}

function renderProjectOptions() {
  const placeholder = `<option value="">${t().selectProject}</option>`
  projectIdSelect.innerHTML = projectsCache.length
    ? `${placeholder}${projectsCache.map((item) => `<option value="${item.id}">${item.name}</option>`).join('')}`
    : placeholder
}

function renderCapabilityState(enabled) {
  createPanel.hidden = !enabled
  inviteAcceptForm.closest('.panel').hidden = !enabled
  invitePanel.hidden = !enabled
  projectPanel.hidden = !enabled
  teamPicker.disabled = !enabled
  inviteTeamSelect.disabled = !enabled
  projectTeamSelect.disabled = !enabled
  projectIdSelect.disabled = !enabled
}

async function loadCapabilitySnapshot() {
  const body = await fetchJson('/api/account/summary')
  if (!body) return false
  capabilitySnapshot = body
  const enabled = capabilityEnabled(body.billing?.capabilities, 'team_collaboration', false)
  renderCapabilityState(enabled)
  if (!enabled) {
    summaryRoot.innerHTML = `<div class="empty">${t().capabilityClosed}</div>`
    membersBody.innerHTML = `<tr><td colspan="4" class="empty">${t().capabilityClosed}</td></tr>`
    teamProjectsBody.innerHTML = `<tr><td colspan="3" class="empty">${t().capabilityClosed}</td></tr>`
    teamInvitesBody.innerHTML = `<tr><td colspan="4" class="empty">${t().capabilityClosed}</td></tr>`
    teamPoliciesRoot.innerHTML = `<div class="empty">${t().capabilityClosed}</div>`
    setStatus(t().capabilityClosed, true)
  }
  return enabled
}

async function loadProjects() {
  const body = await fetchJson('/api/projects')
  if (!body) return
  projectsCache = body.projects || []
  renderProjectOptions()
}

async function loadTeams(preferredTeamId = '') {
  const body = await fetchJson('/api/teams')
  if (!body) return
  teamsCache = body.teams || []
  if (preferredTeamId && teamsCache.some((item) => item.id === preferredTeamId)) {
    selectedTeamId = preferredTeamId
  } else if (selectedTeamId && !teamsCache.some((item) => item.id === selectedTeamId)) {
    selectedTeamId = ''
  }
  renderTeamOptions()
  updateManagementVisibility()
  if (selectedTeamId) {
    await loadOverview(selectedTeamId)
  } else {
    renderEmptyState()
  }
}

function renderEmptyState() {
  summaryRoot.innerHTML = `<div class="empty">${t().emptyTeams}</div>`
  membersBody.innerHTML = `<tr><td colspan="4" class="empty">${t().emptyMembers}</td></tr>`
  teamProjectsBody.innerHTML = `<tr><td colspan="3" class="empty">${t().emptyProjects}</td></tr>`
  teamInvitesBody.innerHTML = `<tr><td colspan="4" class="empty">${t().emptyInvites}</td></tr>`
  teamPoliciesRoot.innerHTML = `<div class="empty">${t().emptyPolicies}</div>`
}

function updateManagementVisibility() {
  const canManage = currentCanManage()
  invitePanel.hidden = !canManage
  projectPanel.hidden = !canManage
  inviteForm.querySelector('button').disabled = !canManage
  projectForm.querySelector('button').disabled = !canManage
}

function renderSummary(overview) {
  const labels = t().summaryLabels
  summaryRoot.innerHTML = `
    <div class="kv-row"><span class="muted">${labels.team}</span><strong>${overview.team.name}</strong></div>
    <div class="kv-row"><span class="muted">${labels.slug}</span><strong>${overview.team.slug}</strong></div>
    <div class="kv-row"><span class="muted">${labels.role}</span><strong>${teamRoleLabel(overview.team.member_role)}</strong></div>
    <div class="kv-row"><span class="muted">${labels.status}</span><span class="pill ok">${overview.team.status}</span></div>
    <div class="kv-row"><span class="muted">${labels.members}</span><strong>${overview.summary.member_count}</strong></div>
    <div class="kv-row"><span class="muted">${labels.projects}</span><strong>${overview.summary.project_count}</strong></div>
    <div class="kv-row"><span class="muted">${labels.invites}</span><strong>${overview.summary.invite_count}</strong></div>
    <div class="kv-row"><span class="muted">${labels.permissions}</span><span class="pill ${overview.team.can_manage ? 'ok' : 'warn'}">${overview.team.can_manage ? t().canManage : t().memberOnly}</span></div>
  `
}

function renderMembers(overview) {
  const canManage = Boolean(overview.team.can_manage)
  membersBody.innerHTML = overview.members?.length
    ? overview.members
        .map((item) => {
          const isOwner = item.role === 'owner'
          const isSelf = item.user_id === capabilitySnapshot?.profile?.id
          const actionLabel = isSelf ? t().leaveTeam : t().removeMember
          const canChangeRole = canManage && !isOwner
          const canRemove = !isOwner && (canManage || isSelf)
          return `
            <tr>
              <td>${item.email || item.user_id}</td>
              <td>
                ${
                  canChangeRole
                    ? `<select data-action="member-role" data-member-id="${item.id}">
                        <option value="member"${item.role === 'member' ? ' selected' : ''}>${teamRoleLabel('member')}</option>
                        <option value="admin"${item.role === 'admin' ? ' selected' : ''}>${teamRoleLabel('admin')}</option>
                      </select>`
                    : `<span class="pill ${isOwner ? 'ok' : ''}">${teamRoleLabel(item.role)}</span>`
                }
              </td>
              <td>${formatDate(item.joined_at)}</td>
              <td>
                <div class="inline-actions">
                  ${
                    canChangeRole
                      ? `<button type="button" class="inline-action" data-action="save-member-role" data-member-id="${item.id}">${t().saveRole}</button>`
                      : ''
                  }
                  ${
                    canRemove
                      ? `<button type="button" class="inline-action" data-action="remove-member" data-member-id="${item.id}">${actionLabel}</button>`
                      : ''
                  }
                </div>
              </td>
            </tr>
          `
        })
        .join('')
    : `<tr><td colspan="4" class="empty">${t().emptyMembers}</td></tr>`
}

function renderProjects(overview) {
  const canManage = Boolean(overview.team.can_manage)
  teamProjectsBody.innerHTML = overview.projects?.length
    ? overview.projects
        .map(
          (item) => `
            <tr>
              <td>${item.project_id}</td>
              <td>${formatDate(item.created_at)}</td>
              <td>
                ${
                  canManage
                    ? `<button type="button" class="inline-action" data-action="detach-project" data-project-id="${item.project_id}">${t().unlinkProject}</button>`
                    : `<span class="muted">${t().viewOnly}</span>`
                }
              </td>
            </tr>
          `,
        )
        .join('')
    : `<tr><td colspan="3" class="empty">${t().emptyProjects}</td></tr>`
}

function renderInvites(overview) {
  teamInvitesBody.innerHTML = overview.invites?.length
    ? overview.invites
        .map(
          (item) => `
            <tr>
              <td>${item.email}</td>
              <td>${teamRoleLabel(item.role)}</td>
              <td><code>${item.invite_token}</code></td>
              <td>${formatDate(item.created_at)}</td>
            </tr>
          `,
        )
        .join('')
    : `<tr><td colspan="4" class="empty">${t().emptyInvites}</td></tr>`
}

function renderPolicies(overview) {
  const policies = overview.policies || {}
  const entries = Object.entries(policies)
  teamPoliciesRoot.innerHTML = entries.length
    ? entries
        .map(
          ([key, value]) =>
            `<div class="kv-row"><span class="muted">${teamPolicyLabel(key)}</span><strong>${value}</strong></div>`,
        )
        .join('')
    : `<div class="empty">${t().emptyPolicies}</div>`
}

async function loadOverview(teamId) {
  if (!teamId) {
    renderEmptyState()
    return
  }
  const body = await fetchJson(`/api/teams/${teamId}/overview`)
  if (!body) return
  teamOverviewCache = body
  setSelectedTeam(teamId)
  const cacheEntry = teamsCache.find((item) => item.id === teamId)
  if (cacheEntry) {
    cacheEntry.can_manage = body.team.can_manage
    cacheEntry.member_role = body.team.member_role
  }
  updateManagementVisibility()
  renderSummary(body)
  renderMembers(body)
  renderProjects(body)
  renderInvites(body)
  renderPolicies(body)
}

async function refreshAll(preferredTeamId = selectedTeamId) {
  await Promise.all([loadProjects(), loadTeams(preferredTeamId)])
}

teamForm.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(teamForm)
  try {
    const body = await fetchJson('/api/teams', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: formData.get('name') }),
    })
    if (!body) return
    teamForm.reset()
    await loadTeams(body.id)
    setStatus(t().created)
  } catch (error) {
    setStatus(error.message || t().createFailed, true)
  }
})

inviteAcceptForm.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(inviteAcceptForm)
  try {
    const body = await fetchJson('/api/teams/invites/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invite_token: formData.get('inviteToken') }),
    })
    if (!body) return
    inviteAcceptForm.reset()
    await loadTeams(body.team_id)
    setStatus(t().inviteAccepted.replace('{team}', body.team_name || ''))
  } catch (error) {
    setStatus(error.message || t().inviteAcceptFailed, true)
  }
})

inviteForm.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(inviteForm)
  try {
    await fetchJson(`/api/teams/${formData.get('teamId')}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: formData.get('email'),
        role: formData.get('role'),
      }),
    })
    await loadOverview(formData.get('teamId'))
    inviteForm.reset()
    setSelectedTeam(formData.get('teamId'))
    renderRoleOptions(inviteForm.querySelector('select[name="role"]'))
    setStatus(t().inviteCreated)
  } catch (error) {
    setStatus(error.message || t().memberFailed, true)
  }
})

projectForm.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(projectForm)
  try {
    await fetchJson(`/api/teams/${formData.get('teamId')}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: formData.get('projectId') }),
    })
    await loadOverview(formData.get('teamId'))
    setSelectedTeam(formData.get('teamId'))
    setStatus(t().projectBound)
  } catch (error) {
    setStatus(error.message || t().bindFailed, true)
  }
})

teamPicker.addEventListener('change', () => {
  setSelectedTeam(teamPicker.value)
  loadOverview(teamPicker.value).catch((error) => setStatus(error.message || t().loadOverviewFailed, true))
})

membersBody.addEventListener('click', async (event) => {
  const button = event.target.closest('button[data-action]')
  if (!button || !teamOverviewCache) return
  const memberId = button.dataset.memberId
  if (!memberId) return
  try {
    if (button.dataset.action === 'save-member-role') {
      const select = membersBody.querySelector(`select[data-member-id="${memberId}"]`)
      const role = select?.value
      if (!role) return
      await fetchJson(`/api/teams/${teamOverviewCache.team.id}/members/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      })
      await loadOverview(teamOverviewCache.team.id)
      setStatus(t().memberRoleSaved)
      return
    }
    if (button.dataset.action === 'remove-member') {
      await fetchJson(`/api/teams/${teamOverviewCache.team.id}/members/${memberId}`, { method: 'DELETE' })
      await loadTeams(teamOverviewCache.team.id)
      setStatus(t().memberRemoved)
    }
  } catch (error) {
    setStatus(error.message || t().memberUpdateFailed, true)
  }
})

teamProjectsBody.addEventListener('click', async (event) => {
  const button = event.target.closest('button[data-action="detach-project"]')
  if (!button || !teamOverviewCache) return
  const projectId = button.dataset.projectId
  if (!projectId) return
  try {
    await fetchJson(`/api/teams/${teamOverviewCache.team.id}/projects/${projectId}`, { method: 'DELETE' })
    await loadOverview(teamOverviewCache.team.id)
    setStatus(t().projectUnlinked)
  } catch (error) {
    setStatus(error.message || t().projectUnlinkFailed, true)
  }
})

function applyLocalizedText() {
  document.title = t().title
  document.querySelector('.dashboard-brand-text')?.replaceChildren(document.createTextNode(t().brandName || '天元计算'))
  document.getElementById('teams-link-account').textContent = t().account
  document.getElementById('teams-link-workspace').textContent = t().workspace
  const ui = t().ui
  document.getElementById('teams-status').textContent = t().loading
  document.getElementById('teams-title-create').textContent = ui.createTeam
  document.getElementById('teams-label-name').childNodes[0].textContent = ui.teamName
  document.getElementById('teams-submit-create').textContent = ui.createTeam
  document.getElementById('teams-title-accept-invite').textContent = ui.acceptInvite
  document.getElementById('teams-label-invite-token').childNodes[0].textContent = ui.inviteToken
  document.getElementById('teams-submit-accept').textContent = ui.acceptInvite
  document.getElementById('teams-title-member').textContent = ui.inviteMember
  document.getElementById('teams-label-member-team').childNodes[0].textContent = ui.team
  document.getElementById('teams-label-member-email').childNodes[0].textContent = ui.memberEmail
  document.getElementById('teams-label-member-role').childNodes[0].textContent = ui.role
  document.getElementById('teams-submit-member').textContent = ui.createInvite
  document.getElementById('teams-title-project').textContent = ui.linkProject
  document.getElementById('teams-label-project-team').childNodes[0].textContent = ui.team
  document.getElementById('teams-label-project-id').childNodes[0].textContent = ui.project
  document.getElementById('teams-submit-project').textContent = ui.linkProject
  document.getElementById('teams-title-overview').textContent = ui.teamOverview
  document.getElementById('teams-copy-overview').textContent = ui.overviewCopy
  document.getElementById('teams-title-members').textContent = ui.members
  document.getElementById('teams-th-email').textContent = ui.email
  document.getElementById('teams-th-role').textContent = ui.role
  document.getElementById('teams-th-joined').textContent = ui.joinedAt
  document.getElementById('teams-th-member-action').textContent = ui.action
  document.getElementById('teams-title-shared-projects').textContent = ui.sharedProjects
  document.getElementById('teams-th-project-id').textContent = ui.projectId
  document.getElementById('teams-th-project-time').textContent = ui.linkedAt
  document.getElementById('teams-th-project-action').textContent = ui.action
  document.getElementById('teams-title-invites').textContent = ui.pendingInvites
  document.getElementById('teams-th-invite-email').textContent = ui.email
  document.getElementById('teams-th-invite-role').textContent = ui.role
  document.getElementById('teams-th-invite-token').textContent = ui.inviteToken
  document.getElementById('teams-th-invite-time').textContent = ui.createdAt
  document.getElementById('teams-title-policy').textContent = ui.policy
  renderRoleOptions(inviteForm.querySelector('select[name="role"]'))
  renderTeamOptions()
  renderProjectOptions()
  if (teamOverviewCache) {
    renderSummary(teamOverviewCache)
    renderMembers(teamOverviewCache)
    renderProjects(teamOverviewCache)
    renderInvites(teamOverviewCache)
    renderPolicies(teamOverviewCache)
  } else if (!teamsCache.length) {
    renderEmptyState()
  }
}

applyLocalizedText()
onLocaleChange(applyLocalizedText)
reportPageView('/teams')

loadCapabilitySnapshot()
  .then((enabled) => {
    if (!enabled) return
    return refreshAll().then(() => {
      setStatus(t().synced)
    })
  })
  .catch((error) => {
    setStatus(error.message || t().genericLoadFailed, true)
  })
